/**
 * Reports API Hooks
 * React hooks for reports endpoints
 * Based on reports-controller from admin-docs.json
 */

import { useFetch, buildUrl, useMutation } from './useApi';
import { ADMIN_API_BASE_URL } from '../config';
import { getAuthToken } from './useAdminRefactored';
import type {
  DateRangeParams,
  RideHistoryResponse,
  RideAnalysisResponse,
  RevenueRideTrendResponse,
  RevenueBreakdownResponse,
  DriverPerformanceTiersResponse,
  CompletedRidesHourlyResponse,
} from './reportsTypes';
import type { Pageable } from './types';

const API_BASE_URL = ADMIN_API_BASE_URL;

/**
 * Get authentication headers
 */
function getAuthHeaders(): Record<string, string> {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ============================================================================
// Ride History Endpoints
// ============================================================================

/**
 * Hook to fetch ride history with pagination
 * GET /reports/ride-history
 */
export function useRideHistory(
  params: DateRangeParams & { pageable: Pageable },
  options: {
    enabled?: boolean;
    onSuccess?: (data: RideHistoryResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryParams: Record<string, any> = {
    page: params.pageable?.page ?? 0,
    size: params.pageable?.size ?? 10,
  };

  if (params.from) {
    queryParams.from = params.from;
  }
  if (params.to) {
    queryParams.to = params.to;
  }
  if (params.pageable?.sort && params.pageable.sort.length > 0) {
    queryParams.sort = params.pageable.sort;
  }

  const url = buildUrl(API_BASE_URL, '/reports/ride-history', queryParams);
  
  return useFetch<RideHistoryResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to export ride history as PDF
 * POST /reports/ride-history-export-pdf
 */
export function useExportRideHistoryPdf(
  options: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<Blob, DateRangeParams>(
    async (params) => {
      const queryParams: Record<string, any> = {};
      if (params.from) {
        queryParams.from = params.from;
      }
      if (params.to) {
        queryParams.to = params.to;
      }

      const url = buildUrl(API_BASE_URL, '/reports/ride-history-export-pdf', queryParams);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response.blob();
    },
    {
      onSuccess: (blob, params) => {
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ride-history-${params.from || 'all'}-${params.to || 'all'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        options.onSuccess?.();
      },
      onError: (error, params) => {
        options.onError?.(error);
      },
    }
  );
}

/**
 * Hook to export ride history as Excel
 * POST /reports/ride-history-export-excel
 */
export function useExportRideHistoryExcel(
  options: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<Blob, DateRangeParams>(
    async (params) => {
      const queryParams: Record<string, any> = {};
      if (params.from) {
        queryParams.from = params.from;
      }
      if (params.to) {
        queryParams.to = params.to;
      }

      const url = buildUrl(API_BASE_URL, '/reports/ride-history-export-excel', queryParams);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response.blob();
    },
    {
      onSuccess: (blob, params) => {
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ride-history-${params.from || 'all'}-${params.to || 'all'}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        options.onSuccess?.();
      },
      onError: (error, params) => {
        options.onError?.(error);
      },
    }
  );
}

// ============================================================================
// Ride Analysis Endpoints
// ============================================================================

/**
 * Hook to fetch total ride analysis
 * GET /reports/ride-analysis
 */
export function useRideAnalysis(
  params: DateRangeParams = {},
  options: {
    enabled?: boolean;
    onSuccess?: (data: RideAnalysisResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryParams: Record<string, any> = {};
  if (params.from) {
    queryParams.from = params.from;
  }
  if (params.to) {
    queryParams.to = params.to;
  }

  const url = buildUrl(API_BASE_URL, '/reports/ride-analysis', queryParams);
  
  return useFetch<RideAnalysisResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

// ============================================================================
// Revenue Endpoints
// ============================================================================

/**
 * Hook to fetch revenue and ride trend for last 6 months
 * GET /reports/revenue-ride-trend-last-six-months
 */
export function useRevenueRideTrend(
  options: {
    enabled?: boolean;
    onSuccess?: (data: RevenueRideTrendResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(API_BASE_URL, '/reports/revenue-ride-trend-last-six-months');
  
  return useFetch<RevenueRideTrendResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch revenue breakdown for last 6 months
 * GET /reports/revenue-last-six-months
 */
export function useRevenueBreakdown(
  options: {
    enabled?: boolean;
    onSuccess?: (data: RevenueBreakdownResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(API_BASE_URL, '/reports/revenue-last-six-months');
  
  return useFetch<RevenueBreakdownResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

// ============================================================================
// Driver Performance Endpoints
// ============================================================================

/**
 * Hook to fetch driver performance tiers
 * GET /reports/driver-performance-tiers
 */
export function useDriverPerformanceTiers(
  options: {
    enabled?: boolean;
    onSuccess?: (data: DriverPerformanceTiersResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(API_BASE_URL, '/reports/driver-performance-tiers');
  
  return useFetch<DriverPerformanceTiersResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

// ============================================================================
// Completed Rides Hourly Endpoints
// ============================================================================

/**
 * Hook to fetch completed rides every 4 hours
 * GET /reports/completed-rides-hourly-4-hours
 */
export function useCompletedRidesHourly(
  date?: string, // ISO date format (YYYY-MM-DD)
  options: {
    enabled?: boolean;
    onSuccess?: (data: CompletedRidesHourlyResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryParams: Record<string, any> = {};
  if (date) {
    queryParams.date = date;
  }

  const url = buildUrl(API_BASE_URL, '/reports/completed-rides-hourly-4-hours', queryParams);
  
  return useFetch<CompletedRidesHourlyResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}
